// client/src/utils/cacheUtils.ts
// OPTIMIZATION PHASE 2.4: Client-side caching utility

import { createChapterMetadata } from './hasChaptersCanonical';

interface CacheEntry<T> {
  data: T;
  timestamp: Date;
  ttl: number; // Time to live in milliseconds
  version?: string;
}

interface CacheConfig {
  projectSummaries: {
    ttl: number; // 5 minutes
  };
  blueprints: {
    ttl: number; // Until modified (24 hours default)
  };
  chapterContent: {
    ttl: number; // 10 minutes
  };
  chapters: {
    ttl: number; // Until modified (24 hours default)
  };
}

const DEFAULT_CONFIG: CacheConfig = {
  projectSummaries: {
    ttl: 5 * 60 * 1000, // 5 minutes
  },
  blueprints: {
    ttl: 24 * 60 * 60 * 1000, // 24 hours
  },
  chapterContent: {
    ttl: 10 * 60 * 1000, // 10 minutes
  },
  chapters: {
    ttl: 24 * 60 * 60 * 1000, // 24 hours
  },
};

class CacheManager {
  private prefix = 'storylikho_cache_';
  private pendingInvalidations = new Map<string, NodeJS.Timeout>();

  private getKey(namespace: string, id: string): string {
    return `${this.prefix}${namespace}_${id}`;
  }

  private isExpired<T>(entry: CacheEntry<T>): boolean {
    return Date.now() - entry.timestamp.getTime() > entry.ttl;
  }

  // Generic cache operations
  set<T>(namespace: string, id: string, data: T, ttl?: number, version?: string): void {
    try {
      const entry: CacheEntry<T> = {
        data,
        timestamp: new Date(),
        ttl: ttl || DEFAULT_CONFIG.projectSummaries.ttl,
        version,
      };

      localStorage.setItem(this.getKey(namespace, id), JSON.stringify(entry));
    } catch (error) {
      console.warn('Failed to cache data:', error);
    }
  }

  get<T>(namespace: string, id: string): T | null {
    try {
      const cached = localStorage.getItem(this.getKey(namespace, id));
      if (!cached) return null;

      const entry: CacheEntry<T> = JSON.parse(cached);
      entry.timestamp = new Date(entry.timestamp); // Restore Date object

      if (this.isExpired(entry)) {
        this.remove(namespace, id);
        return null;
      }

      return entry.data;
    } catch (error) {
      console.warn('Failed to retrieve cached data:', error);
      this.remove(namespace, id);
      return null;
    }
  }

  remove(namespace: string, id: string): void {
    try {
      localStorage.removeItem(this.getKey(namespace, id));
    } catch (error) {
      console.warn('Failed to remove cached data:', error);
    }
  }

  clear(namespace?: string): void {
    try {
      if (namespace) {
        // Clear specific namespace
        const keys = Object.keys(localStorage);
        const prefixToRemove = `${this.prefix}${namespace}_`;
        keys.forEach(key => {
          if (key.startsWith(prefixToRemove)) {
            localStorage.removeItem(key);
          }
        });
      } else {
        // Clear all cache
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
          if (key.startsWith(this.prefix)) {
            localStorage.removeItem(key);
          }
        });
      }
    } catch (error) {
      console.warn('Failed to clear cache:', error);
    }
  }

  // Project summaries caching
  setProjectSummaries(summaries: any[]): void {
    this.set('project_summaries', 'all', summaries, DEFAULT_CONFIG.projectSummaries.ttl);
  }

  getProjectSummaries(): any[] | null {
    return this.get('project_summaries', 'all');
  }

  invalidateProjectSummaries(): void {
    this.remove('project_summaries', 'all');
  }

  // Blueprint caching with automatic timestamp enforcement
  setBlueprint(projectId: string, blueprint: any, version?: string): void {
    // CRITICAL FIX: Ensure blueprint always has proper timestamps for hydration
    const timestampedBlueprint = {
      ...blueprint,
      updatedAt: blueprint.updatedAt || new Date().toISOString(),
      lastModified: blueprint.lastModified || new Date().toISOString(),
      // Ensure blueprintData has timestamp for STEP-7 comparison
      blueprintData: {
        ...blueprint.blueprintData,
        updatedAt: blueprint.blueprintData?.updatedAt || blueprint.updatedAt || new Date().toISOString(),
        // Preserve nested blueprint structure if it exists
        ...(blueprint.blueprintData?.blueprint && {
          blueprint: {
            ...blueprint.blueprintData.blueprint,
            updatedAt: blueprint.blueprintData.blueprint.updatedAt || new Date().toISOString()
          }
        })
      }
    };

    console.log('💾 Blueprint cache write with enforced timestamps:', {
      projectId,
      hasUpdatedAt: !!timestampedBlueprint.updatedAt,
      hasBlueprintDataUpdatedAt: !!timestampedBlueprint.blueprintData?.updatedAt,
      updatedAt: timestampedBlueprint.updatedAt,
      blueprintDataUpdatedAt: timestampedBlueprint.blueprintData?.updatedAt
    });

    this.set('blueprint', projectId, timestampedBlueprint, DEFAULT_CONFIG.blueprints.ttl, version);
  }

  getBlueprint(projectId: string): any | null {
    return this.get('blueprint', projectId);
  }

  invalidateBlueprint(projectId: string): void {
    this.remove('blueprint', projectId);
  }

  // 🚀 SYNCHRONIZED UPDATE: Update all 3 localStorage objects consistently
  updateAllProjectCaches(projectId: string, apiResponse: any, dataType: 'idea-hub' | 'book-metadata' | 'world-building' | 'author-details' | 'front-matter' | 'back-matter' | 'publishing-essentials' | 'chapters-outline'): void {
    console.log('🔄 Synchronized cache update:', {
      projectId,
      dataType,
      apiResponseKeys: Object.keys(apiResponse),
      affectedCaches: ['project-summary', 'blueprint', 'project_summaries_all']
    });

    // 1. Update the partial data first (existing logic)
    this.updateProjectDataPartial(projectId, apiResponse, dataType);

    // 2. Update the project_summaries_all array
    this.updateProjectSummariesAll(projectId, apiResponse);
  }

  // Update the project_summaries_all array with new data
  private updateProjectSummariesAll(projectId: string, apiResponse: any): void {
    try {
      const existingProjectSummaries = this.getProjectSummaries();
      if (existingProjectSummaries) {
        const updatedSummaries = existingProjectSummaries.map((project: any) => {
          if (project.uuid === projectId || project.id?.toString() === projectId) {
            console.log('🔄 Updating project in summaries array:', {
              projectTitle: project.title,
              oldBookType: project.bookType,
              newBookType: apiResponse.bookType || apiResponse.workType,
              oldTargetWordCount: project.targetWordCount,
              newTargetWordCount: apiResponse.targetWordCount,
              hasWorkType: !!apiResponse.workType,
              hasBookType: !!apiResponse.bookType
            });
            
            return {
              ...project,
              // Update fields that can change in IdeaHub
              ...(apiResponse.bookType && { bookType: apiResponse.bookType }),
              ...(apiResponse.workType && { bookType: apiResponse.workType }), // workType maps to bookType in summaries
              ...(apiResponse.targetWordCount !== undefined && { targetWordCount: apiResponse.targetWordCount }),
              // CRITICAL FIX: Update chapters-outline fields in project summaries (but protect existing chapter data)
              ...(apiResponse.hasChapters !== undefined && {
                hasChapters: project.hasChapters || apiResponse.hasChapters // Never downgrade hasChapters
              }),
              ...(apiResponse.chaptersCount !== undefined && {
                chaptersCount: Math.max(project.chaptersCount ?? 0, apiResponse.chaptersCount ?? 0) // Never decrease count
              }),
              ...(apiResponse.chaptersGenerated !== undefined && { chaptersGenerated: apiResponse.chaptersGenerated }),
              ...(apiResponse.chaptersOutline !== undefined && { chaptersOutline: apiResponse.chaptersOutline }),
              ...(apiResponse.chaptersLastUpdate && { chaptersLastUpdate: apiResponse.chaptersLastUpdate }),
              ...(apiResponse.totalExpectedWords !== undefined && { totalExpectedWords: apiResponse.totalExpectedWords }),
              // Update other fields if present
              ...(apiResponse.updatedAt && { updatedAt: apiResponse.updatedAt }),
              ...(apiResponse.id && { id: apiResponse.id }),
              ...(apiResponse.uuid && { uuid: apiResponse.uuid }),
            };
          }
          return project;
        });

        this.setProjectSummaries(updatedSummaries);
      } else {
      }
    } catch (error) {
      console.warn('Failed to update project_summaries_all:', error);
    }
  }

  // 🚀 SOPHISTICATED PARTIAL UPDATE: Handle partial API responses for any data type
  updateProjectDataPartial(projectId: string, apiResponse: any, dataType: 'idea-hub' | 'book-metadata' | 'world-building' | 'author-details' | 'front-matter' | 'back-matter' | 'publishing-essentials' | 'chapters-outline'): void {
    console.log('🔄 Sophisticated partial update:', {
      projectId,
      dataType,
      apiResponseKeys: Object.keys(apiResponse),
      apiResponseId: apiResponse.id,
      apiResponseUuid: apiResponse.uuid
    });

    // Get existing cached data for all relevant cache namespaces
    const existingProjectSummary = this.get<Record<string, any>>('project-summary', projectId);
    const existingBlueprint = this.get<Record<string, any>>('blueprint', projectId);
    
    console.log('🔍 Cache lookup results:', {
      projectId,
      hasProjectSummary: !!existingProjectSummary,
      hasBlueprint: !!existingBlueprint,
      projectSummaryKeys: existingProjectSummary ? Object.keys(existingProjectSummary) : null,
      expectedCacheKey: `storylikho_cache_project-summary_${projectId}`
    });

    // Helper function to deep merge objects preserving nested structures
    const deepMerge = (target: any, source: any): any => {
      if (!source || typeof source !== 'object') return target;
      if (!target || typeof target !== 'object') return source;

      const result = { ...target };
      
      for (const key in source) {
        if (source.hasOwnProperty(key)) {
          if (typeof source[key] === 'object' && source[key] !== null && !Array.isArray(source[key])) {
            result[key] = deepMerge(target[key], source[key]);
          } else {
            result[key] = source[key];
          }
        }
      }
      
      return result;
    };

    // Update project summary cache (used by dashboard and workspace)
    if (existingProjectSummary) {
      const updatedProjectSummary = {
        ...existingProjectSummary,
        id: apiResponse.id || existingProjectSummary.id,
        uuid: apiResponse.uuid || existingProjectSummary.uuid,
        updatedAt: apiResponse.updatedAt || new Date().toISOString(),
        // Merge specific data based on type
        ...(apiResponse.targetWordCount !== undefined && { targetWordCount: apiResponse.targetWordCount }),
        ...(apiResponse.bookType && { bookType: apiResponse.bookType }),
        ...(apiResponse.workType && {
          bookType: apiResponse.workType, // Map workType to bookType for project summary
          workType: apiResponse.workType   // Keep workType as well for compatibility
        }),
        ...(apiResponse.idea && { idea: apiResponse.idea }),
        ...(apiResponse.targetPageCount !== undefined && { targetPageCount: apiResponse.targetPageCount }),
        // CRITICAL FIX: Handle chapters-outline specific fields (protect existing data)
        ...(apiResponse.hasChapters !== undefined && {
          hasChapters: existingProjectSummary.hasChapters || apiResponse.hasChapters // Never downgrade
        }),
        ...(apiResponse.chaptersCount !== undefined && {
          chaptersCount: Math.max(existingProjectSummary.chaptersCount ?? 0, apiResponse.chaptersCount ?? 0) // Never decrease
        }),
        ...(apiResponse.chaptersGenerated !== undefined && { chaptersGenerated: apiResponse.chaptersGenerated }),
        ...(apiResponse.chaptersOutline !== undefined && { chaptersOutline: apiResponse.chaptersOutline }),
        ...(apiResponse.chaptersLastUpdate && { chaptersLastUpdate: apiResponse.chaptersLastUpdate }),
        ...(apiResponse.totalExpectedWords !== undefined && { totalExpectedWords: apiResponse.totalExpectedWords }),
        ...(apiResponse.firstChapterTitle && { firstChapterTitle: apiResponse.firstChapterTitle }),
        ...(apiResponse.lastChapterTitle && { lastChapterTitle: apiResponse.lastChapterTitle }),
        // Deep merge nested objects
        ...(apiResponse.bookMetadata && { 
          bookMetadata: deepMerge(existingProjectSummary.bookMetadata || {}, apiResponse.bookMetadata) 
        }),
        ...(apiResponse.worldBuildingData && { 
          worldBuildingData: deepMerge(existingProjectSummary.worldBuildingData || {}, apiResponse.worldBuildingData) 
        }),
        ...(apiResponse.authorDetails && { 
          authorDetails: deepMerge(existingProjectSummary.authorDetails || {}, apiResponse.authorDetails) 
        }),
        ...(apiResponse.frontMatter && { 
          frontMatter: deepMerge(existingProjectSummary.frontMatter || {}, apiResponse.frontMatter) 
        }),
        ...(apiResponse.backMatter && { 
          backMatter: deepMerge(existingProjectSummary.backMatter || {}, apiResponse.backMatter) 
        }),
        ...(apiResponse.publishingEssentials && { 
          publishingEssentials: deepMerge(existingProjectSummary.publishingEssentials || {}, apiResponse.publishingEssentials) 
        }),
      };

      console.log('✅ Updated existing project summary cache:', {
        dataType,
        oldTargetWordCount: existingProjectSummary.targetWordCount,
        newTargetWordCount: updatedProjectSummary.targetWordCount,
        oldBookType: existingProjectSummary.bookType,
        newBookType: updatedProjectSummary.bookType,
        oldIdea: existingProjectSummary.idea?.substring(0, 50) + '...',
        newIdea: updatedProjectSummary.idea?.substring(0, 50) + '...',
        updatedKeys: Object.keys(apiResponse)
      });

      this.set('project-summary', projectId, updatedProjectSummary, 5 * 60 * 1000);
    } else {
      // Create new project summary cache entry if it doesn't exist
      const newProjectSummary = {
        id: apiResponse.id,
        uuid: apiResponse.uuid || projectId,
        title: apiResponse.title || "Untitled Project",
        updatedAt: apiResponse.updatedAt || new Date().toISOString(),
        createdAt: apiResponse.createdAt || new Date().toISOString(),
        // Add fields from API response
        ...(apiResponse.targetWordCount !== undefined && { targetWordCount: apiResponse.targetWordCount }),
        ...(apiResponse.bookType && { bookType: apiResponse.bookType }),
        ...(apiResponse.workType && { bookType: apiResponse.workType }), // workType maps to bookType
        ...(apiResponse.idea && { idea: apiResponse.idea }),
        ...(apiResponse.targetPageCount !== undefined && { targetPageCount: apiResponse.targetPageCount }),
        ...(apiResponse.hasBlueprint !== undefined && { hasBlueprint: apiResponse.hasBlueprint }),
        // CRITICAL FIX: Handle chapters-outline specific fields for new projects
        ...(apiResponse.hasChapters !== undefined && { hasChapters: apiResponse.hasChapters }),
        ...(apiResponse.chaptersCount !== undefined && { chaptersCount: apiResponse.chaptersCount }),
        ...(apiResponse.chaptersGenerated !== undefined && { chaptersGenerated: apiResponse.chaptersGenerated }),
        ...(apiResponse.chaptersOutline !== undefined && { chaptersOutline: apiResponse.chaptersOutline }),
        ...(apiResponse.chaptersLastUpdate && { chaptersLastUpdate: apiResponse.chaptersLastUpdate }),
        ...(apiResponse.totalExpectedWords !== undefined && { totalExpectedWords: apiResponse.totalExpectedWords }),
        ...(apiResponse.firstChapterTitle && { firstChapterTitle: apiResponse.firstChapterTitle }),
        ...(apiResponse.lastChapterTitle && { lastChapterTitle: apiResponse.lastChapterTitle }),
        // Add nested objects
        ...(apiResponse.bookMetadata && { bookMetadata: apiResponse.bookMetadata }),
        ...(apiResponse.worldBuildingData && { worldBuildingData: apiResponse.worldBuildingData }),
        ...(apiResponse.authorDetails && { authorDetails: apiResponse.authorDetails }),
        ...(apiResponse.frontMatter && { frontMatter: apiResponse.frontMatter }),
        ...(apiResponse.backMatter && { backMatter: apiResponse.backMatter }),
        ...(apiResponse.publishingEssentials && { publishingEssentials: apiResponse.publishingEssentials }),
      };

      console.log('✅ Created new project summary cache:', {
        dataType,
        targetWordCount: newProjectSummary.targetWordCount,
        bookType: newProjectSummary.bookType,
        hasNestedData: {
          bookMetadata: !!newProjectSummary.bookMetadata,
          worldBuildingData: !!newProjectSummary.worldBuildingData
        }
      });

      this.set('project-summary', projectId, newProjectSummary, 5 * 60 * 1000);
    }

    // Update blueprint cache if it exists (used by chapters and detailed views)
    if (existingBlueprint) {
      const updatedBlueprint = {
        ...existingBlueprint,
        id: apiResponse.id || existingBlueprint.id,
        uuid: apiResponse.uuid || existingBlueprint.uuid,
        updatedAt: apiResponse.updatedAt || new Date().toISOString(),
        lastModified: new Date().toISOString(),
        // Update top-level fields
        ...(apiResponse.targetWordCount !== undefined && { targetWordCount: apiResponse.targetWordCount }),
        ...(apiResponse.bookType && { bookType: apiResponse.bookType }),
        ...(apiResponse.workType && { workType: apiResponse.workType }),
        ...(apiResponse.idea && { idea: apiResponse.idea }),
        // Update nested blueprint structure
        blueprint: {
          ...existingBlueprint.blueprint,
          // Deep merge nested objects in blueprint based on API response data
          ...(apiResponse.bookMetadata && {
            bookMetadata: deepMerge(existingBlueprint.blueprint?.bookMetadata || {}, apiResponse.bookMetadata)
          }),
          ...(apiResponse.worldBuildingData && {
            worldBuildingData: deepMerge(existingBlueprint.blueprint?.worldBuildingData || {}, apiResponse.worldBuildingData)
          }),
          ...(apiResponse.authorDetails && {
            authorDetails: deepMerge(existingBlueprint.blueprint?.authorDetails || {}, apiResponse.authorDetails)
          }),
          ...(apiResponse.frontMatter && {
            frontMatter: deepMerge(existingBlueprint.blueprint?.frontMatter || {}, apiResponse.frontMatter)
          }),
          ...(apiResponse.backMatter && {
            backMatter: deepMerge(existingBlueprint.blueprint?.backMatter || {}, apiResponse.backMatter)
          }),
          ...(apiResponse.publishingEssentials && {
            publishingEssentials: deepMerge(existingBlueprint.blueprint?.publishingEssentials || {}, apiResponse.publishingEssentials)
          }),
        },
        // CRITICAL FIX: Update blueprintData.updatedAt that STEP-7 uses for comparison
        blueprintData: {
          ...existingBlueprint.blueprintData,
          updatedAt: apiResponse.updatedAt || new Date().toISOString(),
          // Also preserve the blueprint structure within blueprintData if it exists
          blueprint: {
            ...existingBlueprint.blueprintData?.blueprint,
            // Deep merge nested objects in blueprintData.blueprint based on API response
            ...(apiResponse.bookMetadata && {
              bookMetadata: deepMerge(existingBlueprint.blueprintData?.blueprint?.bookMetadata || {}, apiResponse.bookMetadata)
            }),
            ...(apiResponse.worldBuildingData && {
              worldBuildingData: deepMerge(existingBlueprint.blueprintData?.blueprint?.worldBuildingData || {}, apiResponse.worldBuildingData)
            }),
            ...(apiResponse.authorDetails && {
              authorDetails: deepMerge(existingBlueprint.blueprintData?.blueprint?.authorDetails || {}, apiResponse.authorDetails)
            }),
            ...(apiResponse.frontMatter && {
              frontMatter: deepMerge(existingBlueprint.blueprintData?.blueprint?.frontMatter || {}, apiResponse.frontMatter)
            }),
            ...(apiResponse.backMatter && {
              backMatter: deepMerge(existingBlueprint.blueprintData?.blueprint?.backMatter || {}, apiResponse.backMatter)
            }),
            ...(apiResponse.publishingEssentials && {
              publishingEssentials: deepMerge(existingBlueprint.blueprintData?.blueprint?.publishingEssentials || {}, apiResponse.publishingEssentials)
            }),
          }
        }
      };

      console.log('✅ Updated blueprint cache:', {
        dataType,
        cacheKey: `storylikho_cache_blueprint_${projectId}`,
        updatedFields: Object.keys(apiResponse),
        blueprintBookMetadata: updatedBlueprint.blueprint?.bookMetadata, // Full object, not just tense
        blueprintWorldBuilding: updatedBlueprint.blueprint?.worldBuildingData ? Object.keys(updatedBlueprint.blueprint.worldBuildingData) : null,
        blueprintAuthorDetails: updatedBlueprint.blueprint?.authorDetails ? Object.keys(updatedBlueprint.blueprint.authorDetails) : null,
        blueprintUpdatedAt: updatedBlueprint.updatedAt,
        // CRITICAL: Verify blueprintData.updatedAt is being set for STEP-7
        blueprintDataUpdatedAt: updatedBlueprint.blueprintData?.updatedAt,
        blueprintDataUpdatedAtMatches: updatedBlueprint.blueprintData?.updatedAt === apiResponse.updatedAt
      });

      this.set('blueprint', projectId, updatedBlueprint, 24 * 60 * 60 * 1000);
    }
  }

  // Update blueprint with latest idea-hub data while preserving complete blueprint structure
  updateBlueprintWithIdeaHub(projectId: string, ideaHubData: any): void {
    const existingCachedData = this.getBlueprint(projectId);
    
    console.log('🔄 Cache update debug:', {
      projectId,
      hasExistingData: !!existingCachedData,
      hasApiBlueprint: !!ideaHubData.blueprint,
      apiResponseKeys: Object.keys(ideaHubData),
      existingDataKeys: existingCachedData ? Object.keys(existingCachedData) : []
    });
    
    if (existingCachedData && ideaHubData.blueprint) {
      // Update the cached data structure to match localStorage format
      const updatedCachedData = {
        ...existingCachedData,
        // Update basic fields from API response
        id: ideaHubData.id,
        uuid: ideaHubData.uuid,
        targetWordCount: ideaHubData.targetWordCount,
        workType: ideaHubData.workType,
        idea: ideaHubData.idea,
        hasBlueprint: ideaHubData.hasBlueprint,
        hasInitialBlueprint: true,
        lastModified: new Date().toISOString(),
        // Update blueprint with merged data
        blueprint: {
          ...existingCachedData.blueprint,
          ...ideaHubData.blueprint,
          // Ensure database values take precedence in bookMetadata
          bookMetadata: {
            ...existingCachedData.blueprint?.bookMetadata,
            ...(ideaHubData.blueprint.bookMetadata || {}),
            targetWordCount: ideaHubData.targetWordCount, // Database authority
            literaryForm: ideaHubData.workType // Database authority
          }
        }
      };
      
      console.log('🔄 Updating cache with merged data:', {
        hasBlueprint: !!updatedCachedData.blueprint,
        targetWordCount: updatedCachedData.targetWordCount,
        blueprintTargetWordCount: updatedCachedData.blueprint?.bookMetadata?.targetWordCount
      });
      
      this.setBlueprint(projectId, updatedCachedData);
    } else if (ideaHubData.blueprint) {
      // Store new complete data structure
      const newCachedData = {
        id: ideaHubData.id,
        uuid: ideaHubData.uuid,
        title: ideaHubData.title || "Untitled Project",
        targetWordCount: ideaHubData.targetWordCount,
        workType: ideaHubData.workType,
        idea: ideaHubData.idea,
        hasBlueprint: ideaHubData.hasBlueprint,
        hasInitialBlueprint: true,
        lastModified: new Date().toISOString(),
        blueprint: ideaHubData.blueprint
      };
      
      console.log('🔄 Storing new cache data:', {
        hasBlueprint: !!newCachedData.blueprint,
        targetWordCount: newCachedData.targetWordCount
      });
      
      this.setBlueprint(projectId, newCachedData);
    }
  }

  // Chapters outline caching (full chapters array for a project)
  setChapters(projectId: string, chapters: any[], version?: string): void {
    console.log('💾 Caching chapters for project:', {
      projectId,
      chaptersCount: chapters.length,
      firstChapterTitle: chapters[0]?.title,
      version
    });

    // Enhance chapters with timestamps if missing (for cache bootstrap compatibility)
    const currentTimestamp = new Date().toISOString();
    const enhancedChapters = chapters.map(chapter => ({
      ...chapter,
      // Ensure each chapter has a timestamp for deriveChaptersState
      updatedAt: chapter.updatedAt || currentTimestamp,
      chaptersLastUpdate: chapter.chaptersLastUpdate || currentTimestamp
    }));

    this.set('chapters', projectId, enhancedChapters, DEFAULT_CONFIG.chapters.ttl, version);
  }

  getChapters(projectId: string): any[] | null {
    const chapters = this.get<any[]>('chapters', projectId);
    if (chapters) {
      console.log('✅ Retrieved cached chapters for project:', {
        projectId,
        chaptersCount: chapters.length,
        firstChapterTitle: chapters[0]?.title
      });
    } else {
    }
    return chapters;
  }

  invalidateChapters(projectId: string): void {
    this.remove('chapters', projectId);
  }

  // Update project with chapters metadata after outline generation
  updateProjectWithChapters(projectId: string, chapters: any[], progressData?: any): void {
    console.log('🔄 Updating project caches with chapters metadata:', {
      projectId,
      chaptersCount: chapters.length,
      totalExpectedWords: chapters.reduce((sum: number, ch: any) => sum + (ch.expectedWordCount || 0), 0),
      hasProgressData: !!progressData
    });

    // Calculate canonical chapters metadata
    const canonicalMetadata = createChapterMetadata(chapters);
    const chaptersMetadata = {
      ...canonicalMetadata,
      chaptersLastUpdate: new Date().toISOString(),
      totalExpectedWords: chapters.reduce((sum: number, ch: any) => sum + (ch.expectedWordCount || 0), 0),
      firstChapterTitle: chapters[0]?.title || '',
      lastChapterTitle: chapters[chapters.length - 1]?.title || '',
      // 🎯 INTEGRATION: Include progress data from LLM response if available
      ...(progressData && { progress: progressData })
    };

    // Store chapters array
    this.setChapters(projectId, chapters);

    // Update project summary and blueprint caches with chapters metadata
    this.updateProjectDataPartial(projectId, chaptersMetadata, 'chapters-outline');

    console.log('✅ Project caches updated with chapters metadata:', {
      ...chaptersMetadata,
      progressIncluded: !!progressData,
      chaptersOutlineProgress: progressData?.chaptersOutline || 'from metadata: 100'
    });
  }

  // Chapter content caching
  setChapterContent(chapterId: string, content: any): void {
    this.set('chapter_content', chapterId, content, DEFAULT_CONFIG.chapterContent.ttl);
  }

  getChapterContent(chapterId: string): any | null {
    return this.get('chapter_content', chapterId);
  }

  invalidateChapterContent(chapterId: string): void {
    this.remove('chapter_content', chapterId);
  }

  // Project-specific invalidation
  invalidateProject(projectId: string): void {
    
    // Clear project-specific caches
    this.remove('project-summary', projectId);
    this.invalidateBlueprint(projectId);
    this.invalidateChapters(projectId);
    
    // Clear project summaries to force refresh of dashboard data
    this.invalidateProjectSummaries();
    
    // Clear all chapters for this project (we'd need to track which chapters belong to which project)
    // For now, we'll clear all chapter content to be safe
    this.clear('chapter_content');
    
  }

  // Force refresh all caches for a project (useful when backend data changes directly)
  forceRefreshProject(projectId: string): void {
    this.invalidateProject(projectId);
    
    // Also clear any React Query cache if available
    if (typeof window !== 'undefined' && (window as any).reactQueryClient) {
      const queryClient = (window as any).reactQueryClient;
      queryClient.invalidateQueries(['story-project-summary', projectId]);
      queryClient.invalidateQueries(['project-blueprint', projectId]);
    }
    
  }

  // Cache statistics
  getCacheStats(): {
    totalItems: number;
    totalSize: number; // Approximate size in characters
    namespaces: Record<string, number>;
  } {
    const stats = {
      totalItems: 0,
      totalSize: 0,
      namespaces: {} as Record<string, number>,
    };

    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          stats.totalItems++;
          const value = localStorage.getItem(key) || '';
          stats.totalSize += value.length;

          // Extract namespace
          const namespace = key.replace(this.prefix, '').split('_')[0];
          stats.namespaces[namespace] = (stats.namespaces[namespace] || 0) + 1;
        }
      });
    } catch (error) {
      console.warn('Failed to get cache stats:', error);
    }

    return stats;
  }

  // Clean up expired entries
  cleanup(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          const value = localStorage.getItem(key);
          if (value) {
            try {
              const entry = JSON.parse(value);
              entry.timestamp = new Date(entry.timestamp);
              if (this.isExpired(entry)) {
                localStorage.removeItem(key);
              }
            } catch {
              // Invalid entry, remove it
              localStorage.removeItem(key);
            }
          }
        }
      });
    } catch (error) {
      console.warn('Failed to cleanup cache:', error);
    }
  }

  // 🚀 PERFORMANCE FIX: Batched cache invalidation to prevent excessive React Query rerenders

  /**
   * Schedule a batched cache invalidation to prevent multiple simultaneous invalidations
   * @param queryClient - React Query client instance
   * @param projectId - Project ID for scoped invalidations
   * @param operations - Array of invalidation operations to batch
   * @param delay - Debounce delay in milliseconds (default: 100ms)
   */
  scheduleBatchedInvalidation(
    queryClient: any,
    projectId: string,
    operations: Array<{ queryKey: any[]; type?: 'invalidate' | 'remove' }>,
    delay: number = 100
  ): void {
    const batchKey = `batch_invalidation_${projectId}`;

    // Clear any existing pending invalidation for this project
    const existingTimeout = this.pendingInvalidations.get(batchKey);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Schedule new batched invalidation
    const timeoutId = setTimeout(() => {
      console.log('🔄 Executing batched cache invalidation for project:', projectId, {
        operationCount: operations.length,
        operations: operations.map(op => ({ queryKey: op.queryKey, type: op.type || 'invalidate' }))
      });

      // Execute all invalidation operations in batch
      operations.forEach(({ queryKey, type = 'invalidate' }) => {
        try {
          if (type === 'remove') {
            queryClient.removeQueries({ queryKey });
          } else {
            queryClient.invalidateQueries({ queryKey });
          }
        } catch (error) {
          console.warn('Failed to execute cache operation:', { queryKey, type, error });
        }
      });

      // Clean up the pending invalidation
      this.pendingInvalidations.delete(batchKey);

    }, delay);

    // Store the timeout for potential cleanup
    this.pendingInvalidations.set(batchKey, timeoutId);
  }

  /**
   * Cancel any pending batched invalidations for a project
   * @param projectId - Project ID to cancel invalidations for
   */
  cancelPendingInvalidations(projectId: string): void {
    const batchKey = `batch_invalidation_${projectId}`;
    const existingTimeout = this.pendingInvalidations.get(batchKey);

    if (existingTimeout) {
      clearTimeout(existingTimeout);
      this.pendingInvalidations.delete(batchKey);
    }
  }

  /**
   * Execute immediate cache invalidation without batching (for critical updates)
   * @param queryClient - React Query client instance
   * @param operations - Array of invalidation operations to execute immediately
   */
  immediateInvalidation(
    queryClient: any,
    operations: Array<{ queryKey: any[]; type?: 'invalidate' | 'remove' }>
  ): void {
    console.log('⚡ Executing immediate cache invalidation:', {
      operationCount: operations.length,
      operations: operations.map(op => ({ queryKey: op.queryKey, type: op.type || 'invalidate' }))
    });

    operations.forEach(({ queryKey, type = 'invalidate' }) => {
      try {
        if (type === 'remove') {
          queryClient.removeQueries({ queryKey });
        } else {
          queryClient.invalidateQueries({ queryKey });
        }
      } catch (error) {
        console.warn('Failed to execute immediate cache operation:', { queryKey, type, error });
      }
    });

  }

  /**
   * Clear cache for a specific project (useful for debugging stuck states)
   */
  clearProjectCache(projectId: string): void {

    // Clear all localStorage entries for this project
    this.remove('project-summary', projectId);
    this.remove('blueprint', projectId);
    this.remove('chapters', projectId);

    // Also clear the all projects summary list to force dashboard refresh
    this.invalidateProjectSummaries();

  }
}

// Export singleton instance
export const cacheManager = new CacheManager();

// Helper hook for cache-aware queries
export const useCacheAwareQuery = <T>(
  key: string,
  fetchFn: () => Promise<T>,
  cacheKey: { namespace: string; id: string },
  options?: {
    ttl?: number;
    version?: string;
    onCacheHit?: (data: T) => void;
    onCacheMiss?: () => void;
  }
) => {
  // Check cache first
  const cachedData = cacheManager.get<T>(cacheKey.namespace, cacheKey.id);
  
  if (cachedData) {
    options?.onCacheHit?.(cachedData);
    return {
      data: cachedData,
      isLoading: false,
      isFromCache: true,
    };
  }

  options?.onCacheMiss?.();

  // If not in cache, we'd typically use react-query here
  // For now, return cache miss indication
  return {
    data: null,
    isLoading: true,
    isFromCache: false,
  };
};

// Auto-cleanup on page load
if (typeof window !== 'undefined') {
  // Run cleanup on page load
  setTimeout(() => {
    cacheManager.cleanup();
  }, 1000);

  // Run cleanup every 10 minutes
  setInterval(() => {
    cacheManager.cleanup();
  }, 10 * 60 * 1000);
}